import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import { FormSpy } from "react-final-form";

import { Button, Typography } from "@material-ui/core";

import ContentResourcesQuickLinksStore from "stores/ContentResourcesQuickLinksStore";

import Search from "components/Input/Search";
import FormControl from "components/FormControl";
import LoadingButton from "components/LoadingButton";
import SubmitButtonControl from "components/FormControl/SubmitButtonControl";
import CardListFieldControl from "components/FormControl/CardListFieldControl";
import Loader from "components/Layout/Loader";
import FilteringControl from "components/FilteringControl";
import SortingControl from "components/SortingControl";
import Popup from "components/Popup";
import ConfirmationDialog from "components/ConfirmationDialog";
import { SNACKBAR_TYPES } from "components/Snackbar";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import { TableSortType } from "stores/templates/TableStore";

import { FILTER_COLUMNS } from "constants/filterConstants";
import { PUBLISH_STATUSES } from "constants/models";

import {
  Container,
  Row,
  CardListRow,
  Buttons,
  ButtonContainer,
  SearchControlWrapper,
  SearchControls,
  TitleContainer,
  SubtitleContainer,
} from "./styles";

type FormSubmitData = {
  values: any;
};

type Props = {
  isLoading: boolean;
  categoryId?: number;
  onClose: () => void;
  onChangeIsDirty: (params: { dirty: boolean }) => void;
  onSubmit: (data: FormSubmitData) => void;
  shouldShowUpdateConfirmation: boolean;
} & SnackbarContextProps;

const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class AddQuickLinksForm extends React.Component<Props> {
  @observable updateConfirmationIsOpened: boolean = false;

  @computed get hasMoreInfo() {
    return ContentResourcesQuickLinksStore.hasMoreInfo;
  }

  @computed get searchValue() {
    return ContentResourcesQuickLinksStore.search;
  }

  @computed get options() {
    return ContentResourcesQuickLinksStore.quickLinkOptions;
  }

  componentDidMount() {
    const { categoryId } = this.props;

    ContentResourcesQuickLinksStore.applyFilter({
      column: "NotIncludeInResourceCategoryId",
      value: categoryId,
    });

    ContentResourcesQuickLinksStore.fetch();
  }

  componentWillUnmount() {
    ContentResourcesQuickLinksStore.reset();
  }

  fetchMoreQuickLinks = async () => {
    const { setMessage } = this.props;

    try {
      await ContentResourcesQuickLinksStore.fetchMoreQuickLinks();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    ContentResourcesQuickLinksStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH &&
      ContentResourcesQuickLinksStore.fetch();
  };

  onSearchClear = () => {
    ContentResourcesQuickLinksStore.clearSearch();
    ContentResourcesQuickLinksStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    ContentResourcesQuickLinksStore.applyOrder(value);
    ContentResourcesQuickLinksStore.resetPage();
    ContentResourcesQuickLinksStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      ContentResourcesQuickLinksStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    ContentResourcesQuickLinksStore.resetPage();
    ContentResourcesQuickLinksStore.fetch();
  };

  onFilterClear = () => {
    ContentResourcesQuickLinksStore.clearFilter(FILTER_COLUMNS.ACTIVE_PROGRAMS);
    ContentResourcesQuickLinksStore.clearFilter(FILTER_COLUMNS.PUBLISH_STATUS);
    ContentResourcesQuickLinksStore.fetch();
  };

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  confirmUpdate = (values: any) => () => {
    const { onSubmit } = this.props;

    this.closeUpdateConfirmation();
    onSubmit(values);
  };

  onSubmit = (values: any) => {
    const { onSubmit, shouldShowUpdateConfirmation } = this.props;

    const publishedQuickLinksSelected = values.quickLinks?.some(
      ({ publishStatus }: { publishStatus: string }) =>
        [
          PUBLISH_STATUSES.PUBLISHED,
          PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
        ].includes(publishStatus),
    );

    shouldShowUpdateConfirmation && publishedQuickLinksSelected
      ? this.openUpdateConfirmation()
      : onSubmit(values);
  };

  render() {
    const { onClose, onChangeIsDirty } = this.props;

    return (
      <FormControl onSubmit={this.onSubmit}>
        {this.updateConfirmationIsOpened && (
          <FormSpy subscription={{ values: true }}>
            {({ values }) => (
              <Popup onClose={this.closeUpdateConfirmation} withoutExitButton>
                <ConfirmationDialog
                  title="Save Changes to Production"
                  text="All changes made will be published to production. Are you sure you want to proceed?"
                  acceptText="Proceed"
                  declineText="Cancel"
                  onAccept={this.confirmUpdate(values)}
                  onDecline={this.closeUpdateConfirmation}
                />
              </Popup>
            )}
          </FormSpy>
        )}
        <FormSpy
          subscription={{ dirty: true, values: true }}
          onChange={onChangeIsDirty}
        />
        <Container>
          <FormSpy subscription={{ values: true }}>
            {({ values }) => {
              const selectedItemsCount = values?.quickLinks?.length;

              return (
                <Row>
                  <TitleContainer>
                    <Typography variant="h1" color="textPrimary">
                      Add Quick Link
                    </Typography>
                    {!!selectedItemsCount && (
                      <SubtitleContainer>
                        <Typography variant="subtitle1" color="textPrimary">
                          {`${selectedItemsCount} items selected`}
                        </Typography>
                      </SubtitleContainer>
                    )}
                  </TitleContainer>
                </Row>
              );
            }}
          </FormSpy>

          <Row>
            <Search
              initValue={ContentResourcesQuickLinksStore.search}
              onApply={this.onSearchApply}
              onClear={this.onSearchClear}
            />
            <SearchControls>
              <SearchControlWrapper>
                <SortingControl
                  initValue={
                    ContentResourcesQuickLinksStore.orderContext
                      .appliedOrderInfo
                  }
                  onApply={this.onSortApply}
                  disablePortal
                />
              </SearchControlWrapper>
              <SearchControlWrapper>
                <FilteringControl
                  initValue={ContentResourcesQuickLinksStore.filters}
                  onApply={this.onFilterApply}
                  onClear={this.onFilterClear}
                  statusFiltering
                />
              </SearchControlWrapper>
            </SearchControls>
          </Row>

          <CardListRow id="scrollableDiv">
            {!ContentResourcesQuickLinksStore.isLoading ? (
              <CardListFieldControl
                name="quickLinks"
                options={this.options}
                hasMore={this.hasMoreInfo}
                fetchMore={this.fetchMoreQuickLinks}
                searchValue={this.searchValue}
                noItemsPlaceholderText={"No quick links added yet."}
                scrollableTarget="scrollableDiv"
                multi
                withInfinityScroll
              />
            ) : (
              <Loader />
            )}
          </CardListRow>
        </Container>

        <Buttons>
          <ButtonContainer>
            <Button
              variant="outlined"
              color="primary"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
          </ButtonContainer>
          <ButtonContainer>
            <SubmitButtonControl render={this.renderButton} />
          </ButtonContainer>
        </Buttons>
      </FormControl>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={this.props.isLoading}
      fullWidth
    >
      Add
    </LoadingButton>
  );
}

export default withGlobalSnackbar(AddQuickLinksForm);
